// @flow
import React, {PureComponent, Children, cloneElement, createRef} from 'react';
import Draggable from 'react-draggable';
import {CSSTransition, TransitionGroup} from 'react-transition-group';
import debounce from 'lodash.debounce';
import isEqual from 'lodash.isequal';

import ElementGroup from '@pb/kit-component-element-group';
import {BasicIconButton} from '@pb/kit-component-icon-button';
import {Icons, Sizes} from '@pb/kit-component-icon';
import {shouldUpdate} from '@pb/kit-util-should-update';
import {convertPxToRem} from '@pb/kit-util-convert-px-to-rem';
import {hashedClasses, withCssModule} from '@pb/kit-util-with-css-module';

import {ANIMATION_TIME, animationTransition} from './config';
import styles from './carousel.component.module.styl';
import carouselItemStyles from './carousel-item/carousel-item.module.styl';

import type {DraggableData} from 'react-draggable';

import type {
    State,
    CarouselChildren,
    MoveDirectionType,
    SlideParameters,
    ViewportParams,
    CarouselPosition,
    AfterChange,
    ChangeParamsType,
    OnDragStop
} from './typedef';

const classNames = withCssModule(styles);
const classes = hashedClasses(styles);

export type Props = {|
    /**
     * You can define whether or not to show the arrows
     */
    arrows: boolean,
    /**
     * Slides to scroll at once
     */
    slidesToScroll: number,
    /**
     * Set to scroll the current number of visible slides in viewport
     */
    scrollByViewport: boolean,
    /**
     * Callback which will start after changing of a slide
     */
    afterChange?: AfterChange,
    /**
     * Animation duration in milliseconds
     */
    speed: number,
    /**
     * Enable scrollable via dragging
     */
    draggable: boolean,
    children: CarouselChildren,
    /**
     * Handle on drag stop
     */
    onDragStop?: OnDragStop,
    /**
     * Space between two slick carousel items, in px
     */
    slidesGap: number,
    /**
     * Add gap before the first slide and after the last slide
     */
    addSlidesTrackSideGaps: boolean,
    /**
     * @deprecated It will be removed in 6 months 09-15-2025. use `onChangePosition` instead
     */
    onDirectionChange?: (direction: string) => void,
    /**
     *  ClassName for Carousel
     */
    mix: string,
    /**
     * Handle on click of the direction arrow buttons
     */
    onChangePosition?: (params: ChangeParamsType) => void,
    /**
     * Handle after change list position
     */
    onChangeListPosition?: () => void,
|};

export const CAROUSEL_MOVE_DIRECTION = {
    LEFT: 'LEFT',
    RIGHT: 'RIGHT'
};

export const CAROUSEL_POSITION = {
    START: 'START',
    CENTER: 'CENTER',
    END: 'END'
};

const SLIDES_TO_SCROLL_DEFAULT = 1;
const ANIMATION_TIMEOUT = 200;
const CALCULATE_VIEWPORT_DEBOUNCE_TIMEOUT = 200;
const CAROUSEL_LIST_CLASSNAME = 'carousel__list';
const DIFFERENCE_FOR_MOVING = 20;

function isPrevNextChildrenEqual(prevChildren: CarouselChildren, nextChildren: CarouselChildren) {
    const prevChildrenIds = Children.toArray(prevChildren).map(child => child.props.id);
    const nextChildrenIds = Children.toArray(nextChildren).map(child => child.props.id);
    return isEqual(prevChildrenIds, nextChildrenIds);
}

export class Carousel extends PureComponent<Props, State> {
    slidesParameters: Array<SlideParameters>;
    slidesInViewPort: Array<SlideParameters>;
    carouselListWidth: number;
    carouselMoveDirection: MoveDirectionType;
    carouselPosition: CarouselPosition;
    viewport: ?HTMLDivElement;
    viewportParams: ViewportParams;
    slidesNodeMap: Map<string, ?HTMLDivElement>;
    numberOfEnterSlide: number;
    observer: ?ResizeObserver;
    handleOnAnimationEnd: () => void;
    handleRecalculateListPositionIfEmptySpace: () => void;
    initingAddSlideId: ?string;

    nodeRef = createRef<HTMLDivElement>();

    static defaultProps = {
        arrows: true,
        slidesToScroll: SLIDES_TO_SCROLL_DEFAULT,
        scrollByViewport: true,
        speed: 1500,
        draggable: true,
        slidesGap: 0,
        addSlidesTrackSideGaps: true,
        mix: ''
    };

    constructor(props: Props) {
        super(props);

        this.slidesParameters = [];
        this.slidesInViewPort = [];
        this.carouselListWidth = 0;
        this.carouselMoveDirection = CAROUSEL_MOVE_DIRECTION.RIGHT;
        this.carouselPosition = CAROUSEL_POSITION.CENTER;
        this.viewportParams = {
            width: 0,
            startPos: 0,
            endPos: 0
        };
        this.slidesNodeMap = new Map();
        this.numberOfEnterSlide = 0;
        this.handleOnAnimationEnd = debounce(this.calculateSlidesParameters, ANIMATION_TIMEOUT);
        this.handleRecalculateListPositionIfEmptySpace = debounce(
            this.recalculateListPositionIfEmptySpace, ANIMATION_TIME);
        this.initingAddSlideId = null;


        this.state = {
            speed: props.speed,
            listPosition: 0,
            currentSlideIndex: 0,
            arrowsDisabledPrev: false,
            arrowsDisabledNext: false,
            draggable: props.draggable,
            children: this.cloneAndWrapChildren(props)
        };
    }

    componentDidMount() {
        if (this.props.children) {
            this.calculateSlidesParameters();
            this.observer = new ResizeObserver(
                debounce(this.viewportAndListRecalculations, CALCULATE_VIEWPORT_DEBOUNCE_TIMEOUT)
            );
            if (this.viewport) {
                this.observer.observe(this.viewport);
            }
        }
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        const {arrowsDisabledPrev, arrowsDisabledNext} = this.state;
        const {afterChange, speed, draggable, children, onChangeListPosition} = this.props;

        if (shouldUpdate(prevProps, this.props, ['speed', 'draggable'])) {
            this.setState({speed, draggable});
        }

        if (!isPrevNextChildrenEqual(prevProps.children, children)) {
            this.setState({children: this.cloneAndWrapChildren(this.props)});
        }

        if (afterChange) {
            afterChange({arrows: {prev: arrowsDisabledPrev, next: arrowsDisabledNext}});
        }

        if (prevState.listPosition !== this.state.listPosition && onChangeListPosition) {
            onChangeListPosition();
        }

        this.calculateSlidesInViewport();
        this.resetViewportScrollLeft(); // TODO: need to fix shifting of carousel after update
    }

    componentWillUnmount() {
        if (this.observer) {
            this.observer.disconnect();
        }
    }

    calculateSlidesParameters = () => {
        this.numberOfEnterSlide = 0;
        this.carouselListWidth = 0;

        this.slidesParameters = Children.toArray(this.props.children).map((child, index, array) => {
            const {id} = child.props;
            const node = this.slidesNodeMap.get(id);
            if (node) {
                const {offsetLeft, offsetWidth, offsetHeight} = node;
                const lastSlideGaps = this.props.addSlidesTrackSideGaps ? this.props.slidesGap * 2 : 0;
                const slidesGap = index !== array.length - 1 ? this.props.slidesGap : lastSlideGaps;
                this.carouselListWidth += slidesGap + offsetWidth;

                return {
                    id,
                    startPos: offsetLeft,
                    finishPos: offsetLeft + offsetWidth,
                    width: offsetWidth,
                    height: offsetHeight,
                    node
                };
            }

            return {id: '', startPos: 0, finishPos: 0, width: 0, height: 0, node: null};
        });

        this.viewportAndListRecalculations();
    };

    viewportAndListRecalculations = () => {
        this.calculateSlidesInViewport();
        this.recalculateListPositionAfterAddedNewSlides();
        this.handleRecalculateListPositionIfEmptySpace();
    };

    calculateViewportParams() {
        const width = this.viewport ? this.viewport.offsetWidth : this.viewportParams.width;
        const startPos = this.state.listPosition;
        let endPos = startPos + width;

        if (this.isViewportWithLastSlides(this.state.currentSlideIndex, width)) {
            endPos = this.carouselListWidth;
        }

        this.viewportParams = {width, startPos, endPos};
    }

    calculateSlidesInViewport = () => {
        this.calculateViewportParams();

        this.slidesInViewPort = this.slidesParameters.filter(slide =>
            slide.startPos >= this.viewportParams.startPos && slide.finishPos <= this.viewportParams.endPos
        );
        this.updateArrows();
    };

    recalculateListPositionIfEmptySpace = () => {
        const {listPosition} = this.state;
        const widthOfEmptySpace = this.viewportParams.width - (this.carouselListWidth - listPosition);

        if (widthOfEmptySpace > 0) {
            this.showViewportWithLastSlides();
        }
    };

    recalculateListPositionAfterAddedNewSlides = () => {
        const lastSlideInViewport = this.slidesInViewPort[this.slidesInViewPort.length - 1];
        if (lastSlideInViewport && lastSlideInViewport.id === this.initingAddSlideId) {
            const lastSlideInViewportIndex = this.slidesParameters.findIndex(
                slide => slide.id === lastSlideInViewport.id);
            this.goToSlide(lastSlideInViewportIndex);
        }
        this.initingAddSlideId = null;
    };

    resetViewportScrollLeft() { // TODO: need to fix shifting of carousel after update
        if (this.viewport && this.viewport.scrollLeft) {
            this.viewport.scrollLeft = 0;
        }
    }

    isViewportWithLastSlides(slideIndex: number, width: number) {
        const {slidesGap, addSlidesTrackSideGaps} = this.props;
        const currentSlides = this.slidesParameters.slice(slideIndex);

        const widthOfNextSlides = currentSlides.reduce((sum, slide, index) => {
            const currentSlideGap = !addSlidesTrackSideGaps && (index === currentSlides.length - 1) ? 0 : slidesGap;

            return sum + slide.width + currentSlideGap;
        }, addSlidesTrackSideGaps ? slidesGap : 0);

        return widthOfNextSlides < width;
    }

    isSlideInViewport(startPosition: number) {
        return startPosition >= this.viewportParams.startPos && startPosition <= this.viewportParams.endPos;
    }

    toPrevSlide = () => {
        this.carouselMoveDirection = CAROUSEL_MOVE_DIRECTION.LEFT;
        let prevSlideIndex = 0;

        if (this.props.scrollByViewport) {
            const idOfFirstSlideInViewport = this.slidesInViewPort[0].id;
            const indexOfFirstSlideInViewPort = this.slidesParameters
                .findIndex(slide => slide.id === idOfFirstSlideInViewport);
            prevSlideIndex = indexOfFirstSlideInViewPort - this.slidesInViewPort.length;
        } else {
            prevSlideIndex = this.state.currentSlideIndex - this.props.slidesToScroll;
        }

        this.carouselPosition = this.getCarouselPositionOnClick(prevSlideIndex);

        if (this.props.onDirectionChange) {
            this.props.onDirectionChange(CAROUSEL_MOVE_DIRECTION.LEFT);
        }

        if (this.props.onChangePosition) {
            this.props.onChangePosition({
                direction: CAROUSEL_MOVE_DIRECTION.LEFT,
                position: this.carouselPosition
            });
        }
        this.goToSlide(prevSlideIndex > 0 ? prevSlideIndex : 0);
    };

    toNextSlide = () => {
        this.carouselMoveDirection = CAROUSEL_MOVE_DIRECTION.RIGHT;
        const indexOfLastSlideInCarousel = this.slidesParameters.length - 1;
        let nextSlideIndex = 0;

        if (this.props.scrollByViewport) {
            const idOfLastSlideInViewport = this.slidesInViewPort[this.slidesInViewPort.length - 1].id;
            const indexOfLastSlideInViewPort = this.slidesParameters.findIndex(
                slide => slide.id === idOfLastSlideInViewport);
            nextSlideIndex = indexOfLastSlideInViewPort + 1;
        } else {
            nextSlideIndex = this.state.currentSlideIndex + this.props.slidesToScroll;
        }

        this.carouselPosition = this.getCarouselPositionOnClick(nextSlideIndex);

        if (this.props.onDirectionChange) {
            this.props.onDirectionChange(CAROUSEL_MOVE_DIRECTION.RIGHT);
        }

        if (this.props.onChangePosition) {
            this.props.onChangePosition({
                direction: CAROUSEL_MOVE_DIRECTION.RIGHT,
                position: this.carouselPosition
            });
        }
        this.goToSlide(nextSlideIndex < this.slidesParameters.length ? nextSlideIndex : indexOfLastSlideInCarousel);
    };

    goToSlide = (slideIndex: number) => {
        const slidesGap = this.props.addSlidesTrackSideGaps ? this.props.slidesGap : 0;
        const nextSlideIndex = slideIndex;
        const nextListPosition = this.slidesParameters[nextSlideIndex].startPos - slidesGap;

        if (this.isViewportWithLastSlides(nextSlideIndex, this.viewportParams.width)) {
            this.showViewportWithLastSlides();
        } else {
            this.changeStateOfList(nextSlideIndex, nextListPosition);
        }
    };

    showViewportWithLastSlides() {
        const nextListPosition = this.carouselListWidth - this.viewportParams.width;
        const nextSlideIndex = this.slidesParameters.length - this.slidesInViewPort.length;

        this.changeStateOfList(nextSlideIndex, nextListPosition);
    }

    changeStateOfList(currentSlideIndex: number, listPosition: number) {
        this.setState({currentSlideIndex, listPosition: Math.max(listPosition, 0)});
    }

    getCarouselListStyles = () => {
        const {listPosition, speed} = this.state;

        return {
            transform: `translateX(-${listPosition})`,
            transition: `all ${speed}ms`
        };
    };

    updateArrows = () => {
        if (this.slidesInViewPort.length > 0) {
            const firstSlideInViewport = this.slidesInViewPort[0];
            const firstSlideInCarousel = this.slidesParameters[0];
            const lastSlideInViewport = this.slidesInViewPort[this.slidesInViewPort.length - 1];
            const lastSlideInCarousel = this.slidesParameters[this.slidesParameters.length - 1];

            const arrowsDisabledPrev = firstSlideInViewport.id === firstSlideInCarousel.id;
            const arrowsDisabledNext = lastSlideInViewport.id === lastSlideInCarousel.id;
            if (
                this.state.arrowsDisabledPrev !== arrowsDisabledPrev ||
                this.state.arrowsDisabledNext !== arrowsDisabledNext) {
                this.setState({
                    arrowsDisabledPrev,
                    arrowsDisabledNext
                });
            }
        }
    };

    getIndexOfNextSlideByListPosition = (listPosition: number) => (
        this.slidesParameters.findIndex(slide => {
            let slideIndex = 0;

            switch (this.carouselMoveDirection) {
                case CAROUSEL_MOVE_DIRECTION.RIGHT:
                    slideIndex = slide.startPos >= listPosition && listPosition < slide.finishPos;
                    break;

                case CAROUSEL_MOVE_DIRECTION.LEFT:
                    slideIndex = slide.startPos <= listPosition && listPosition < slide.finishPos;
                    break;
            }

            return slideIndex;
        })
    );

    handleOnDragStart = () => {
        this.setState({speed: 50});
    };

    getCarouselPositionOnClick = (nextSlideIndex: number) => {
        if (nextSlideIndex <= 0) {
            return CAROUSEL_POSITION.START;
        }

        if (this.slidesParameters.length - nextSlideIndex <= this.slidesInViewPort.length) {
            return CAROUSEL_POSITION.END;
        }

        return CAROUSEL_POSITION.CENTER;
    };

    handleOnDrag = (e: Event, data: DraggableData) => {
        this.carouselMoveDirection = (Math.abs(data.x) > this.state.listPosition && data.x < 0) ?
            CAROUSEL_MOVE_DIRECTION.RIGHT : CAROUSEL_MOVE_DIRECTION.LEFT;
    };

    handleOnDragStop = (e: Event, data: DraggableData) => {
        const nextListPosition = data.x < 0 ? Math.abs(data.x) : -data.x;
        let nextSlideIndex = this.getIndexOfNextSlideByListPosition(nextListPosition);

        if (data.x <= 0 && Math.abs(data.x) >= this.carouselListWidth && nextSlideIndex < 0
            || nextSlideIndex === this.slidesParameters.length - 1) {
            nextSlideIndex = this.slidesParameters.length - this.slidesInViewPort.length;
        }

        if (data.x >= 0 && nextSlideIndex < 0) {
            nextSlideIndex = 0;
        }

        if (nextSlideIndex === -1) {
            nextSlideIndex = this.slidesParameters.length - this.slidesInViewPort.length;
        }

        this.carouselPosition = this.getCarouselPositionOnClick(nextSlideIndex);

        this.setState({speed: this.props.speed}, () => {
            if (nextSlideIndex >= 0 && nextSlideIndex <= this.slidesParameters.length - 1) {
                this.goToSlide(nextSlideIndex);
            }
        });

        if (Math.abs(nextListPosition - this.state.listPosition) > DIFFERENCE_FOR_MOVING && this.props.onDragStop) {
            this.props.onDragStop({
                direction: this.carouselMoveDirection,
                position: this.carouselPosition
            });
        }
    };

    initAddAnimation = (slideId: string) => {
        this.initingAddSlideId = slideId;
    };

    initRemoveAnimation = (slideId: string) => {
        const item = this.slidesParameters.find(slide => slide.id === slideId);
        this.runExitChildAnimation(item && item.node);
    };

    runEnterChildAnimation = (node: ?HTMLElement) => {
        if (node && this.initingAddSlideId) {
            const {width, left} = node.getBoundingClientRect();
            const startPosition = this.state.listPosition + left;

            if (this.isSlideInViewport(startPosition)) {
                const {style} = node;
                const maxWidth = convertPxToRem(width);
                style.transition = animationTransition;
                style.maxWidth = '0';
                style.marginLeft = convertPxToRem(this.props.slidesGap) || style.marginLeft;

                if (maxWidth) {
                    setTimeout(() => {
                        style.maxWidth = maxWidth;
                    }, ANIMATION_TIMEOUT);
                }
            }
        }
    };

    runExitChildAnimation = (node: ?HTMLElement) => {
        if (node) {
            const {style} = node;
            const width = convertPxToRem(node.offsetWidth);
            style.width = width || '0';
            style.transition = animationTransition;

            setTimeout(() => {
                style.width = '0';
                style.marginLeft = '0';
                style.marginRight = '0';
                style.opacity = '0';
            }, ANIMATION_TIMEOUT);
        }
    };

    getSlideMargins = (index: number, slidesGap: number, slidesCount: number) => {
        const {addSlidesTrackSideGaps} = this.props;
        const slideGapInRem = convertPxToRem(slidesGap);
        const childStyle = {marginLeft: slideGapInRem};

        if (addSlidesTrackSideGaps) {
            const lastChildStyle = {...childStyle, marginRight: slideGapInRem};
            return index !== slidesCount - 1 ? childStyle : lastChildStyle;
        }

        return index > 0 ? childStyle : {};
    };

    cloneAndWrapChildren = ({children, slidesGap}: Props) => {
        this.slidesNodeMap = new Map();

        return Children.toArray(children).map<React$Element<typeof CSSTransition>>((child, index, array) => {
            const {id} = child.props;
            const slideRef = createRef<HTMLDivElement>();

            return (
                <CSSTransition
                    key={id}
                    nodeRef={slideRef}
                    timeout={ANIMATION_TIME}
                    classNames={{
                        enter: carouselItemStyles['carousel-item-enter'],
                        enterActive: carouselItemStyles['carousel-item-enter-active'],
                        exit: carouselItemStyles['carousel-item-exit'],
                        exitActive: carouselItemStyles['carousel-item-exit-active']
                    }}
                    onEnter={this.runEnterChildAnimation}
                    onEntered={this.handleOnAnimationEnd}
                    onExited={this.handleOnAnimationEnd}>
                    {cloneElement(child, {
                        getRef: (el: ?HTMLDivElement) => {
                            slideRef.current = el || null;
                            this.slidesNodeMap.set(id, el || null);
                        },
                        style: this.getSlideMargins(index, slidesGap, array.length),
                        initAddAnimation: this.initAddAnimation,
                        initRemoveAnimation: this.initRemoveAnimation
                    })}
                </CSSTransition>);
        });
    };

    resetListPosition = () => {
        this.setState({listPosition: 0});
    };

    render() {
        if (!this.props.children) {
            return null;
        }

        const {arrows, mix} = this.props;
        const isDraggableDisabled = !this.state.draggable || this.carouselListWidth <= this.viewportParams.width;
        const carouselClasses = classNames('carousel', {[mix]: mix});

        return (
            <div className={carouselClasses}>
                {arrows ?
                    <ElementGroup>
                        <BasicIconButton
                            mix={classes.carousel__prev}
                            icon={Icons.CHEVRON_LEFT}
                            iconSize={Sizes.Size_14}
                            disabled={this.state.arrowsDisabledPrev}
                            handler={this.toPrevSlide}
                        />
                        <BasicIconButton
                            mix={classes.carousel__next}
                            icon={Icons.CHEVRON_RIGHT}
                            iconSize={Sizes.Size_14}
                            disabled={this.state.arrowsDisabledNext}
                            handler={this.toNextSlide}
                        />
                    </ElementGroup> :
                    null}
                <div className={classes.carousel__viewport} ref={el => this.viewport = el}>
                    <Draggable
                        axis="x"
                        nodeRef={this.nodeRef}
                        handle={`.${CAROUSEL_LIST_CLASSNAME}`}
                        disabled={isDraggableDisabled}
                        defaultPosition={{x: 0, y: 0}}
                        position={{x: -this.state.listPosition, y: 0}}
                        onStart={this.handleOnDragStart}
                        onDrag={this.handleOnDrag}
                        onStop={this.handleOnDragStop}>
                        <div
                            ref={this.nodeRef}
                            style={this.getCarouselListStyles()}>
                            <TransitionGroup className={classes[CAROUSEL_LIST_CLASSNAME]}>
                                {this.state.children}
                            </TransitionGroup>
                        </div>
                    </Draggable>
                </div>
            </div>
        );
    }
}

            // @pb/generate-flow-typed Webpack Loader:
            // Duplicates all internal dependencies imports
            // to be sure all deps are parsed by this loader
            // cause Babel loader removes `import type ...`
            // and e.g. 'typedef.js' files are not included in dist
            
require("./typedef");
